type PdfJsLike = {
  getDocument: (source: any) => { promise: Promise<any> };
  OPS: Record<string, number>;
};

type CutContourData = {
  width: number;
  height: number;
  paths: string[];
  groupIds: string[];
  bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  };
};

function applyMatrix(point: [number, number], matrix: number[]) {
  const [x, y] = point;
  return [
    matrix[0] * x + matrix[2] * y + matrix[4],
    matrix[1] * x + matrix[3] * y + matrix[5],
  ];
}

function multiplyMatrix(a: number[], b: number[]) {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}

function buildSvgPath(rawCommands: any, matrix: number[], onPoint?: (x: number, y: number) => void) {
  const commands = Array.isArray(rawCommands) ? rawCommands : Array.from(rawCommands || []);
  let index = 0;
  let path = '';

  while (index < commands.length) {
    const code = Number(commands[index++]);

    if (code === 0) {
      const [x, y] = applyMatrix([Number(commands[index++]), Number(commands[index++])], matrix);
      onPoint?.(x, y);
      path += `M ${x.toFixed(3)} ${y.toFixed(3)} `;
    } else if (code === 1) {
      const [x, y] = applyMatrix([Number(commands[index++]), Number(commands[index++])], matrix);
      onPoint?.(x, y);
      path += `L ${x.toFixed(3)} ${y.toFixed(3)} `;
    } else if (code === 2) {
      const [x1, y1] = applyMatrix([Number(commands[index++]), Number(commands[index++])], matrix);
      const [x2, y2] = applyMatrix([Number(commands[index++]), Number(commands[index++])], matrix);
      const [x3, y3] = applyMatrix([Number(commands[index++]), Number(commands[index++])], matrix);
      onPoint?.(x1, y1);
      onPoint?.(x2, y2);
      onPoint?.(x3, y3);
      path += `C ${x1.toFixed(3)} ${y1.toFixed(3)} ${x2.toFixed(3)} ${y2.toFixed(3)} ${x3.toFixed(3)} ${y3.toFixed(3)} `;
    } else if (code === 3) {
      path += 'Z ';
    } else {
      break;
    }
  }

  return path.trim();
}

function isCutlineStrokeColor(color: string | null) {
  if (!color) return false;
  const normalized = color.toLowerCase();
  return [
    '#e6007e',
    '#fb3099',
    '#ff00ff',
    '#ec008c',
    '#ef4444',
    'rgb(230,0,126)',
    'rgb(251,48,153)',
  ].some((value) => normalized.replace(/\s+/g, '').includes(value));
}

function isCutContourName(name: string) {
  return /cut\s*contour|cutcontour|snij|stans|kiss\s*cut|through\s*cut/i.test(String(name || ''));
}

function cmykToRgb(c: number, m: number, y: number, k: number) {
  const cyan = Math.max(0, Math.min(1, c));
  const magenta = Math.max(0, Math.min(1, m));
  const yellow = Math.max(0, Math.min(1, y));
  const black = Math.max(0, Math.min(1, k));

  const r = Math.round(255 * (1 - cyan) * (1 - black));
  const g = Math.round(255 * (1 - magenta) * (1 - black));
  const b = Math.round(255 * (1 - yellow) * (1 - black));
  return `rgb(${r},${g},${b})`;
}

async function extractCutContourData(pdfjsLib: PdfJsLike, pdfUrl: string): Promise<CutContourData | null> {
  const loadingTask = pdfjsLib.getDocument({
    url: pdfUrl,
    disableWorker: true,
  });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  const optionalContentConfig = await pdf.getOptionalContentConfig?.().catch?.(() => null);
  const operatorList = await page.getOperatorList();
  const ops = pdfjsLib.OPS;
  const viewport = page.getViewport({ scale: 1 });
  const width = Number(viewport.width || 1);
  const height = Number(viewport.height || 1);
  const baseMatrix = Array.isArray(viewport.transform) ? viewport.transform : [1, 0, 0, 1, 0, 0];
  const matrixStack: number[][] = [];
  const cutLayerStack: boolean[] = [];
  let currentMatrix = [...baseMatrix];
  let activeCutLayer = false;
  let currentStrokeColor: string | null = null;
  const paths: string[] = [];
  const groupIds = new Set<string>();
  const bounds = {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
  const trackPoint = (x: number, y: number) => {
    bounds.minX = Math.min(bounds.minX, x);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxX = Math.max(bounds.maxX, x);
    bounds.maxY = Math.max(bounds.maxY, y);
  };

  for (let index = 0; index < operatorList.fnArray.length; index += 1) {
    const fn = operatorList.fnArray[index];
    const args = operatorList.argsArray[index] || [];

    if (fn === ops.save) {
      matrixStack.push([...currentMatrix]);
      cutLayerStack.push(activeCutLayer);
    } else if (fn === ops.restore) {
      currentMatrix = matrixStack.pop() || [...baseMatrix];
      activeCutLayer = cutLayerStack.pop() || false;
    } else if (fn === ops.transform) {
      currentMatrix = multiplyMatrix(currentMatrix, args);
    } else if (fn === ops.setStrokeRGBColor) {
      currentStrokeColor = String(args?.[0] || '');
    } else if (fn === ops.setStrokeCMYKColor) {
      currentStrokeColor = cmykToRgb(Number(args?.[0]), Number(args?.[1]), Number(args?.[2]), Number(args?.[3]));
    } else if (fn === ops.setStrokeColor || fn === ops.setStrokeColorN) {
      currentStrokeColor = String(args?.[0] || args?.join?.(',') || '');
    } else if (fn === ops.beginMarkedContentProps) {
      const groupId = args?.[1]?.id;
      const groupName = groupId ? optionalContentConfig?.getGroup?.(groupId)?.name : '';
      activeCutLayer = activeCutLayer || isCutContourName(String(groupName || ''));
      if (activeCutLayer && groupId) groupIds.add(String(groupId));
    } else if (fn === ops.endMarkedContent) {
      activeCutLayer = cutLayerStack[cutLayerStack.length - 1] || false;
    } else if ((activeCutLayer || isCutlineStrokeColor(currentStrokeColor)) && fn === ops.constructPath) {
      const paintOp = Number(args[0]);
      const shouldStroke = [
        ops.stroke,
        ops.closeStroke,
        ops.fillStroke,
        ops.eoFillStroke,
        ops.closeFillStroke,
        ops.closeEOFillStroke,
      ].includes(paintOp);

      if (!shouldStroke) continue;

      const path = buildSvgPath(args[1]?.[0] || args[1], currentMatrix, trackPoint);
      if (path) paths.push(path);
    }
  }

  if (paths.length === 0) return null;

  return { width, height, paths, groupIds: Array.from(groupIds), bounds };
}

export async function createCutContourHiddenOptionalContentConfig(pdf: any) {
  const optionalContentConfig = await pdf.getOptionalContentConfig?.().catch?.(() => null);
  if (!optionalContentConfig?.setVisibility) return null;

  let changed = false;
  try {
    for (const [id, group] of optionalContentConfig as Iterable<[string, any]>) {
      if (isCutContourName(String(group?.name || ''))) {
        optionalContentConfig.setVisibility(id, false, true);
        changed = true;
      }
    }
  } catch {
    return null;
  }

  return changed ? optionalContentConfig : null;
}

export async function extractCutContourSvg(pdfjsLib: PdfJsLike, pdfUrl: string) {
  const data = await extractCutContourData(pdfjsLib, pdfUrl);
  if (!data) return null;

  const { width, height, paths, bounds } = data;
  const strokeWidth = Math.max(width, height) / 450;
  const dashArray = `${Math.max(width, height) / 120} ${Math.max(width, height) / 180}`;
  const outsidePath = `M 0 0 H ${width} V ${height} H 0 Z ${paths.join(' ')}`;
  const centerX = Number.isFinite(bounds.minX) ? (bounds.minX + bounds.maxX) / 2 : width / 2;
  const centerY = Number.isFinite(bounds.minY) ? (bounds.minY + bounds.maxY) / 2 : height / 2;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" preserveAspectRatio="none">
  <path id="outside-cut-mask" d="${outsidePath}" fill="#ffffff" fill-rule="evenodd" />
  <g id="cut-contour" fill="none" stroke="#ef4444" stroke-width="${strokeWidth}" stroke-dasharray="${dashArray}">
    ${paths.map((path) => `<path d="${path}" />`).join('\n    ')}
  </g>
  <g id="safe-zone" fill="none" stroke="#22c55e" stroke-width="${strokeWidth}" stroke-dasharray="${dashArray}" transform="translate(${centerX} ${centerY}) scale(0.94) translate(${-centerX} ${-centerY})">
    ${paths.map((path) => `<path d="${path}" />`).join('\n    ')}
  </g>
</svg>`;
}

export async function extractCutContourMaskSvg(pdfjsLib: PdfJsLike, pdfUrl: string) {
  const data = await extractCutContourData(pdfjsLib, pdfUrl);
  if (!data) return null;

  const { width, height, paths } = data;
  const outsidePath = `M 0 0 H ${width} V ${height} H 0 Z ${paths.join(' ')}`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" preserveAspectRatio="none">
  <path d="${outsidePath}" fill="#ffffff" fill-rule="evenodd" />
</svg>`;
}
